import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { Fragment, useMemo } from "react";

import { CustomerOrderItemModel } from "@/api/mappers/customerOrderList.mapper";
import { OrderImage } from "@/components/Account/OrderList/Order/OrderItem/partials/orderImage.component";
import { Divider } from "@/components/General/Divider/divider.component";
import { Row } from "@/components/General/Grid/row.component";
import OrderItemStatus from "@/components/Order/shared/OrderItemStatus/orderItemStatus.component";
import { AdditionalServiceComponent } from "@/components/Product/AdditionalService/additionalService.component";
import { ProductConfigurationDetailComponent } from "@/components/Product/ProductConfigurationDetail/productConfigurationDetail.component";
import { ConfigurableProductRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductName } from "@/components/Product/ProductName/productName.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { formatPrice } from "@/utils/price.utils";
import { getProductConfigurationPreview } from "@/utils/productConfiguration";
import { getProductConfiguration } from "@/utils/productConfiguration/productConfiguration.utils";

type Props = {
    item: CustomerOrderItemModel;
    index: number;
    orderId: string;
};

const OrderItem = ({ item, index, orderId }: Props) => {
    const { t } = useGranite();

    const productConfiguration = useMemo(
        () => getProductConfiguration({ sku: item.sku }),
        [item.sku]
    );

    return (
        <>
            {index > 0 && <Divider marginTop="2" />}
            <Row className="d-md-none pt-3">
                <div className="col">
                    <ProductName
                        name={item.title}
                        url={item.url}
                    />
                    {!!item.articleConfiguration && (
                        <div>
                            <ProductConfigurationDetailComponent
                                configuration={item.articleConfiguration}
                            />
                        </div>
                    )}
                </div>
            </Row>
            <Row className="py-3">
                <div className="col-5 col-sm-4 col-md-2 px-md-4">
                    <OrderImage
                        medias={item.medias}
                        title={item.title}
                        url={item.url}
                        imageRenditions={
                            item.articleConfiguration
                                ? ConfigurableProductRenditions
                                : undefined
                        }
                    />
                </div>
                <div className="col-4 col-md-7 d-none d-md-flex flex-column ">
                    <Row className="d-none d-md-block">
                        <div className="col">
                            <ProductName
                                name={`${
                                    item.title
                                }  ${getProductConfigurationPreview(
                                    item.configuration,
                                    productConfiguration
                                )}`}
                                url={item.url}
                            />
                            {!!item.articleConfiguration && (
                                <div>
                                    <ProductConfigurationDetailComponent
                                        configuration={
                                            item.articleConfiguration
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </Row>
                    {item.deliveryType === DeliveryType.ClickAndCollect && (
                        <Row>
                            <div className="col-auto text-nowrap">
                                <OrderItemStatus
                                    statuses={item.status}
                                    deliveryType={item.deliveryType}
                                />
                            </div>
                        </Row>
                    )}
                    {item.deliveryType === DeliveryType.HomeDelivery &&
                        item.transportPackageStatuses.map((status) => (
                            <Fragment
                                key={`f-${orderId}-${status.productSku}-${status.transportStatusMessage}`}
                            >
                                <Row>
                                    <div className="col-auto text-nowrap">
                                        <OrderItemStatus
                                            statuses={item.status}
                                            deliveryType={item.deliveryType}
                                            transportStatusMessage={
                                                status.transportStatusMessage
                                            }
                                        />
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col justify-content-end">
                                        {status.trackAndTraceUrl && (
                                            <a href={status.trackAndTraceUrl!}>
                                                {t("general.trackAndTrace")}
                                            </a>
                                        )}
                                    </div>
                                </Row>
                            </Fragment>
                        ))}
                </div>
                <div className="col">
                    <Row>
                        <div className="col-auto text-md-left">
                            {item.quantity}
                        </div>
                        <div className="col text-right font-weight-bold pr-md-3">
                            <span>{formatPrice(item.totalPrice)}</span>
                        </div>
                    </Row>
                    <Row className="d-md-none text-nowrap">
                        {item.deliveryType === DeliveryType.ClickAndCollect && (
                            <OrderItemStatus
                                statuses={item.status}
                                deliveryType={item.deliveryType}
                            />
                        )}
                        {item.deliveryType === DeliveryType.HomeDelivery &&
                            item.transportPackageStatuses.map((status) => (
                                <Fragment
                                    key={`f-${status.productSku}-${status.transportStatusMessage}`}
                                >
                                    <OrderItemStatus
                                        key={`status-${status.productSku}-${status.transportStatusMessage}`}
                                        statuses={item.status}
                                        deliveryType={item.deliveryType}
                                        transportStatusMessage={
                                            status.transportStatusMessage
                                        }
                                    />
                                    {status.trackAndTraceUrl && (
                                        <div
                                            key={`tnt-${status.productSku}-${status.transportStatusMessage}`}
                                        >
                                            <a href={status.trackAndTraceUrl!}>
                                                {t("general.trackAndTrace")}
                                            </a>
                                        </div>
                                    )}
                                </Fragment>
                            ))}
                    </Row>
                </div>
            </Row>
            {item.additionalServices?.map((service) => (
                <Row
                    key={service.id}
                    className={"pb-3 pl-md-4 pr-md-3"}
                >
                    <div className={"col-9 pl-md-0"}>
                        <AdditionalServiceComponent
                            additionalService={service}
                        />
                    </div>
                    <div
                        className={
                            "col-3 mt-2 text-right font-weight-bold my-auto"
                        }
                    >
                        {formatPrice(service.price)}
                    </div>
                </Row>
            ))}
        </>
    );
};

export default OrderItem;
